import { apiDelete, apiGetOne, apiListAll, apiPatch, apiPost, newestLive } from '@/core/api/resources'
import { ApiError, captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import { descriptionFor } from '@/variants'
import { currentVariant } from './session'
import { extFromFile, moveFile, removeFile, uploadFile, versionedFile } from './storage'
import { fileNamePart } from '@/core/lib/files'
import { isSheet } from '@/core/lib/sheets'

export type DocFields = {
  daten?: Record<string, unknown>
  bucketAddress?: string
  fileType?: string
  fileSize?: number

  teilnehmerSichtbar?: boolean
}

export type DokumentCreate = {
  coaching_id: string
  name: string
  daten: Record<string, unknown>
  bucket_address: string
  file_type: string
  file_size: number
  teilnehmer_sichtbar?: boolean
}
export type DokumentPatch = {
  daten?: Record<string, unknown>
  bucket_address?: string
  file_type?: string
  file_size?: number
  teilnehmer_sichtbar?: boolean
}

async function liveDoc<Q extends Parameters<typeof apiListAll<'/dokumente'>>[1]>(query: Q) {
  return newestLive(await apiListAll('/dokumente', query))
}

async function createDokument(row: DokumentCreate, identity: WriteIdentity = captureWriteIdentity()): Promise<string> {
  const { subtype } = descriptionFor(currentVariant())
  if (subtype) return subtype.createDokument(row, identity)
  return (await apiPost('/dokumente', row, identity)).id
}

export async function getDocData(
  coachingId: string,
  name: string,
): Promise<Record<string, unknown> | null> {
  const row = await liveDoc({ coaching_id: coachingId, name })
  return (row?.daten as Record<string, unknown> | null | undefined) ?? null
}

export async function getDocBucketAddress(coachingId: string, name: string): Promise<string | null> {
  const row = await liveDoc({ coaching_id: coachingId, name })
  return row?.bucket_address ? row.bucket_address : null
}

export type StoredDoc = { id: string; bucketAddress: string; fileType: string; sitzungDatum: string; sheet: boolean; ersetzt?: boolean }

export async function getStoredDoc(coachingId: string, name: string): Promise<StoredDoc | null> {
  const row = await liveDoc({ coaching_id: coachingId, name, include: ['sitzung'] })
  return row
    ? {
        id: row.id,
        bucketAddress: row.bucket_address ?? '',
        fileType: row.file_type ?? '',
        sitzungDatum: row.sitzung?.[0]?.datum ?? '',
        sheet: isSheet(row),
      }
    : null
}

export async function getStoredDocById(id: string): Promise<StoredDoc> {
  const row = await apiGetOne('/dokumente/{id}', { id }, { include: ['sitzung'] })
  return {
    id: row.id,
    bucketAddress: row.bucket_address ?? '',
    fileType: row.file_type ?? '',
    sitzungDatum: row.sitzung?.[0]?.datum ?? '',
    sheet: isSheet(row),
    ersetzt: !!row.ersetzt_am,
  }
}

export const hasStoredFile = (doc: { bucketAddress: string; sheet: boolean }): boolean =>
  doc.sheet || doc.bucketAddress !== ''

export async function docRowExists(coachingId: string, name: string): Promise<boolean> {
  const row = await liveDoc({ coaching_id: coachingId, name })
  return row != null
}

export type SignaturEmbed = { created_at: string; rolle: string }

// Only the participant signature means the document is signed and frozen.
export function participantSignature(
  signaturen: readonly SignaturEmbed[] | null | undefined,
): SignaturEmbed | undefined {
  return signaturen?.find((s) => s.rolle === 'teilnehmer')
}

export async function getDocSignedAt(coachingId: string, name: string): Promise<string | null> {

  const row = await liveDoc({
    coaching_id: coachingId,
    name,
    include: ['signatur'],
  })
  const createdAt = participantSignature(row?.signatur)?.created_at
  return createdAt ? createdAt : null
}

export type DocSnapshot = {
  daten: Record<string, unknown> | null

  bucketAddress: string
  signed: boolean
}

export async function getDocSnapshot(coachingId: string, name: string): Promise<DocSnapshot | null> {
  const row = await liveDoc({
    coaching_id: coachingId,
    name,
    include: ['signatur'],
  })
  if (!row) return null
  return {
    daten: (row.daten as Record<string, unknown> | null) ?? null,
    bucketAddress: row.bucket_address ?? '',
    signed: participantSignature(row.signatur) !== undefined,
  }
}

export type DocRow = {
  id: string
  name: string
  bucketAddress: string
  fileType: string
  sitzungDatum: string

  sheet: boolean

  ersetztAm?: string

  createdAt: string

  signiert?: boolean

  abgegeben?: boolean
}

export async function listDocs(coachingId: string): Promise<DocRow[]> {

  const rows = await apiListAll('/dokumente', {
    coaching_id: coachingId,
    include: ['sitzung', 'signatur'],
  })
  return rows.map((d) => ({
    id: d.id,
    name: d.name,
    bucketAddress: d.bucket_address ?? '',
    fileType: d.file_type ?? '',
    sitzungDatum: d.sitzung?.[0]?.datum ?? '',
    sheet: isSheet(d),
    ersetztAm: d.ersetzt_am ?? '',
    createdAt: d.created_at,
    signiert: participantSignature(d.signatur) !== undefined,
    abgegeben: d.signatur?.some((s) => s.rolle === 'abgabe') ?? false,
  }))
}

export class EntfernenAbgelehnt extends Error {
  constructor(
    message: string,
    readonly weg: boolean,
  ) {
    super(message)
    this.name = 'EntfernenAbgelehnt'
  }
}

export async function removeUploadedDoc(dokumentId: string): Promise<void> {
  try {
    await apiDelete('/documents/{dokument_id}', { dokument_id: dokumentId })
  } catch (e) {
    if (e instanceof ApiError && [404, 409, 502].includes(e.status) && e.detailText) throw new EntfernenAbgelehnt(e.detailText, e.status === 404)
    throw e
  }
}

export async function storeUploadedDoc(coachingId: string, name: string, file: File): Promise<string> {
  const bucketAddress = await uploadFile(file, versionedFile(fileNamePart(name), extFromFile(file)))
  try {
    return await insertDoc({ coachingId, name, bucketAddress, fileType: file.type, fileSize: file.size })
  } catch (e) {
    await removeFile(bucketAddress).catch((cleanup: unknown) =>
      console.warn(`Datei ${bucketAddress} konnte nach der abgelehnten Zeile nicht entfernt werden:`, cleanup),
    )
    throw e
  }
}

export async function insertDoc(row: {
  coachingId: string
  name: string
  daten?: Record<string, unknown>
  bucketAddress?: string
  fileType?: string
  fileSize?: number
}, identity?: WriteIdentity): Promise<string> {
  return createDokument({
    coaching_id: row.coachingId,
    name: row.name,
    daten: row.daten ?? {},
    bucket_address: row.bucketAddress ?? '',
    file_type: row.fileType ?? '',
    file_size: row.fileSize ?? 0,
  }, identity)
}

export async function upsertDoc(
  coachingId: string,
  name: string,
  fields: DocFields,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  const { subtype } = descriptionFor(currentVariant())

  if (fields.teilnehmerSichtbar !== undefined && !subtype) {
    throw new Error(
      `upsertDoc(${coachingId}, ${name}): teilnehmerSichtbar has no target — the current variant has no subtype`,
    )
  }

  const existing = await liveDoc({ coaching_id: coachingId, name })

  if (existing) {
    const patch: DokumentPatch = {}
    if (fields.daten !== undefined) patch.daten = fields.daten
    if (fields.bucketAddress !== undefined) patch.bucket_address = fields.bucketAddress
    if (fields.fileType !== undefined) patch.file_type = fields.fileType
    if (fields.fileSize !== undefined) patch.file_size = fields.fileSize
    if (fields.teilnehmerSichtbar !== undefined) patch.teilnehmer_sichtbar = fields.teilnehmerSichtbar

    if (subtype) await subtype.patchDokument(existing.id, patch, identity)
    else await apiPatch('/dokumente/{id}', { id: existing.id }, patch, identity)
  } else {
    await createDokument({
      coaching_id: coachingId,
      name,
      daten: fields.daten ?? {},
      bucket_address: fields.bucketAddress ?? '',
      file_type: fields.fileType ?? '',
      file_size: fields.fileSize ?? 0,
      ...(fields.teilnehmerSichtbar !== undefined && {
        teilnehmer_sichtbar: fields.teilnehmerSichtbar,
      }),
    }, identity)
  }
}

export async function patchDocData(
  coachingId: string,
  name: string,
  patch: Record<string, unknown>,
  fields?: Omit<DocFields, 'daten'>,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  const existing = await getDocData(coachingId, name)
  await upsertDoc(coachingId, name, { ...fields, daten: { ...(existing ?? {}), ...patch } }, identity)
}

export async function relocateDocObject(fromKey: string, toKey: string): Promise<string> {
  if (fromKey === toKey) return fromKey
  try {
    return await moveFile(fromKey, toKey)
  } catch (e) {
    console.warn(`Datei konnte nicht nach ${toKey} verschoben werden (bleibt unter ${fromKey}):`, e)
    return fromKey
  }
}

export async function renameDoc(dokumentId: string, name: string, identity?: WriteIdentity): Promise<void> {
  await apiPatch('/dokumente/{id}', { id: dokumentId }, { name }, identity)
}
